import type { TelemetryEvent } from "../types";

/**
 * v0.2 telemetry stub. PRD §10 wants three layers exported as JSONL + CSV;
 * the real exporter ships in v0.4 along with Tauri's filesystem bridge.
 *
 * For now:
 *   - Events are appended to an in-memory buffer.
 *   - On flush() we serialise to JSONL and stash in localStorage under
 *     `roamling.session.<id>.jsonl` so a manual peek confirms it's alive.
 *   - Layer 3 (per-second derived sample) is sampled by RunnerScene calling
 *     emit(3, "derived.sample", { skill, challenge, flow }) once per second.
 */
export class TelemetryService {
  private readonly buffer: TelemetryEvent[] = [];
  private readonly startedAt: number;
  private readonly sessionId: string;

  constructor(sessionId: string) {
    this.sessionId = sessionId;
    this.startedAt = performance.now();
  }

  /** Emit an event. Cheap on the hot path; serialisation is deferred to flush(). */
  emit(layer: 1 | 2 | 3, tag: string, data?: TelemetryEvent["data"]): void {
    const event: TelemetryEvent = {
      t: Math.round(performance.now() - this.startedAt),
      layer,
      tag,
      ...(data ? { data } : {}),
    };
    this.buffer.push(event);
  }

  /** For dev: copy of the buffer (e.g. to render an end-of-session debug list). */
  snapshot(): readonly TelemetryEvent[] {
    return this.buffer;
  }

  /** Persist buffer to localStorage as JSONL. Called from EndScene. */
  flush(): void {
    const jsonl = this.buffer.map((e) => JSON.stringify(e)).join("\n");
    try {
      localStorage.setItem(`roamling.session.${this.sessionId}.jsonl`, jsonl);
      console.info(
        `[telemetry] flushed ${this.buffer.length} events to localStorage[roamling.session.${this.sessionId}.jsonl]`,
      );
    } catch (err) {
      console.warn("[telemetry] flush failed:", err);
    }
  }
}
